import React, { useEffect, useState } from "react";
import Header from "./components/Header";
import FavoriteCitiesFloating from "./components/FavoriteCitiesFloating";
import { useWeatherData } from "./hooks/useWeatherData";
import DotNavigation from "./components/DotNavigation";
import { WeatherDashboard } from "./components/WeatherDashboard";
import "./index.css";
import { BrowserRouter, Routes, Route, useParams, useLocation, Navigate, useNavigate } from "react-router-dom";

// Helper component to extract city from path or query
function WeatherAppWrapper({ sections }) {
  const { cityParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate(); // Add useNavigate
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedCity, setSelectedCity] = useState(null);
  const [activeSection, setActiveSection] = useState("current-weather");
  const { currentWeather, forecast, status, error, processedData } =
    useWeatherData(selectedDate, selectedCity);

  // Parse city from /city/:cityParam or from query string
  useEffect(() => {
    let cityName = null;
    if (cityParam) {
      cityName = cityParam;
    } else {
      const params = new URLSearchParams(location.search);
      cityName = params.get("city");
    }

    async function setCityFromIP() {
      try {
        const res = await fetch("https://ipapi.co/json/");
        const data = await res.json();
        let city = "Unknown";
        if (data && typeof data.city === "string" && data.city.trim()) {
          city = data.city;
        } else if (data && data.city && typeof data.city === "object" && data.city.name) {
          city = data.city.name;
        } else if (data && data.city) {
          city = String(data.city);
        } else if (data && data.region) {
          city = data.region;
        }
        setSelectedCity({ name: city });
      } catch {
        setSelectedCity({ name: "Unknown" });
      }
    }

    // Only set selectedCity if it's a string and not an object
    if (cityName && typeof cityName === "string") {
      const selectedCityName = typeof selectedCity?.name === "string" ? selectedCity.name.toLowerCase() : "";
      const cityNameLc = cityName.toLowerCase();
      if (!selectedCity || selectedCityName !== cityNameLc) {
        setSelectedCity({ name: cityName });
      }
    } else {
      // If no city in URL, use IP location city
      if (!selectedCity) {
        setCityFromIP();
      } else {
        const selectedCityName = typeof selectedCity?.name === "string" ? selectedCity.name.toLowerCase() : "";
        const locationLc = typeof location === "string" ? location.toLowerCase() : "";
        if (selectedCityName !== locationLc && typeof location === "string") {
          setSelectedCity({ name: location });
        }
      }
    }
  }, [cityParam, location.search, location, selectedCity]);

  const handleCitySelect = (city) => {
    if (city && typeof city.name === "string") {
      setSelectedCity({ name: city.name });
      navigate(`/${city.name}`); // Update URL path to /London (no /city prefix)
    } else {
      setSelectedCity(null);
      navigate(`/`);
    }
  };

  const handleSectionClick = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setActiveSection(sectionId);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveSection(sections[0].id);
  }, [sections]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      let closestSection = sections[0];
      let minDistance = Infinity;
      sections.forEach((section) => {
        const element = document.getElementById(section.id);
        if (element) {
          const elementTop = element.offsetTop;
          const elementBottom = elementTop + element.offsetHeight;
          const elementCenter = (elementTop + elementBottom) / 2;
          const distance = Math.abs(scrollPosition - elementCenter);
          if (distance < minDistance) {
            minDistance = distance;
            closestSection = section;
          }
        }
      });
      if (window.scrollY > 0) {
        setActiveSection(closestSection.id);
      } else {
        setActiveSection(sections[0].id);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        Loading...
      </div>
    );
  }
  if (status === "failed") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        Error: {error}
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-100">
      <Header onCitySelect={handleCitySelect} selectedCity={selectedCity} />
      <WeatherDashboard
        currentWeather={currentWeather}
        forecast={forecast}
        processedData={processedData}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
      />
      <DotNavigation
        sections={sections}
        activeSection={activeSection}
        onSectionClick={handleSectionClick}
      />
      <FavoriteCitiesFloating onCitySelect={handleCitySelect} />
    </div>
  );
}

function App() {
  const sections = [
    { id: "current-weather", name: "Current Weather" },
    { id: "weather-alerts", name: "Weather Alerts" },
    { id: "main-weather", name: "Main Weather" },
    { id: "weather-history", name: "Weather History" },
    { id: "detailed-weather", name: "Detailed Weather" },
    { id: "additional-weather", name: "Additional Weather" },
    { id: "sun-timeline", name: "Sun Timeline" },
    { id: "moon-timeline", name: "Moon Timeline" },
    { id: "charts", name: "Charts" },
  ];
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/:cityParam" element={<WeatherAppWrapper sections={sections} />} />
        <Route path="/" element={<WeatherAppWrapper sections={sections} />} />
        {/* Redirect /?city=... to /London for consistency */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
